class ProductGalleryZoom extends HTMLElement {
    connectedCallback() {
        this.viewport = this.querySelector("[data-zoom-viewport]")
        this.track = this.querySelector("[data-zoom-track]")
        this.slides = Array.from(this.querySelectorAll("[data-zoom-slide]"))
        this.dotsContainer = this.querySelector("[data-zoom-dots]")
        this.prevButton = this.querySelector("[data-zoom-prev]")
        this.nextButton = this.querySelector("[data-zoom-next]")
        this.cursor = this.querySelector("[data-zoom-cursor]")

        if (!this.slides.length) return

        // Click on a mouse, double-tap on touch, and a ceiling for pinch. All
        // three are authored on the element so they can be tuned per product
        // without touching this file.
        this.zoomScale = parseFloat(this.dataset.zoomScale) || 3
        this.mobileZoomScale =
            parseFloat(this.dataset.zoomScaleMobile) || this.zoomScale
        this.maxScale = Math.max(
            parseFloat(this.dataset.zoomMaxScale) || 3.5,
            this.zoomScale,
            this.mobileZoomScale
        )

        this.dragThreshold = 6
        this.doubleTapMs = 300
        this.doubleTapDistance = 30

        // scale is continuous so pinch can land anywhere between fit and
        // maxScale; 1 means fit-to-frame. zoomed is derived from it.
        this.scale = 1
        this.pan = null
        this.pointers = new Map()
        this.pinch = null
        this.lastTap = null
        this.lastIndex = 0
        this.carouselDragEnabled = true // Embla's own default
        this.transform = { x: 0, y: 0 }

        this.onKeydown = this.onKeydown.bind(this)
        this.onPointerDown = this.onPointerDown.bind(this)
        this.onPointerMove = this.onPointerMove.bind(this)
        this.onPointerUp = this.onPointerUp.bind(this)
        this.onCursorMove = this.onCursorMove.bind(this)
        this.onCursorLeave = this.onCursorLeave.bind(this)

        this.addEventListener("click", (event) => {
            if (event.target.closest("[data-zoom-close]")) this.close()
        })

        // -webkit-user-drag (see the snippet's style block) is WebKit/Blink
        // only, so Firefox needs this to stop the native image drag hijacking
        // a pan.
        this.addEventListener("dragstart", (event) => event.preventDefault())

        this.viewport.addEventListener("pointerdown", this.onPointerDown, true)
        window.addEventListener("pointermove", this.onPointerMove, {
            passive: false,
        })
        window.addEventListener("pointerup", this.onPointerUp)
        window.addEventListener("pointercancel", this.onPointerUp)

        this.initCursor()
    }

    disconnectedCallback() {
        document.removeEventListener("keydown", this.onKeydown)
        window.removeEventListener("pointermove", this.onPointerMove)
        window.removeEventListener("pointerup", this.onPointerUp)
        window.removeEventListener("pointercancel", this.onPointerUp)
        this.removeEventListener("pointermove", this.onCursorMove)
        this.removeEventListener("pointerleave", this.onCursorLeave)
        this.embla?.destroy()
        this.embla = null
    }

    // ---------- custom cursor ----------
    initCursor() {
        if (!this.cursor) return

        if (!window.matchMedia("(pointer: fine)").matches) return

        this.classList.add("has-custom-cursor")

        this.addEventListener("pointermove", this.onCursorMove)
        this.addEventListener("pointerleave", this.onCursorLeave)
    }

    onCursorMove(event) {
        if (!this.cursor) return

        const overImage =
            this.viewport.contains(event.target) &&
            !event.target.closest("button")

        if (!overImage) {
            this.cursor.classList.remove("is-active")
            return
        }

        const rect = this.getBoundingClientRect()
        this.cursor.style.transform = `translate3d(${event.clientX - rect.left}px, ${event.clientY - rect.top}px, 0)`
        this.cursor.classList.add("is-active")
        this.syncCursorIcon()
    }

    onCursorLeave() {
        this.cursor?.classList.remove("is-active")
    }

    syncCursorIcon() {
        this.cursor?.classList.toggle("is-zoom-out", this.zoomed)
    }

    get zoomed() {
        return this.scale > 1
    }

    isOpen() {
        return this.classList.contains("open")
    }

    // ---------- open / close ----------
    open(index = 0) {
        if (!this.slides.length) return

        this.classList.add("open")
        this.setAttribute("aria-hidden", "false")
        document.body.classList.add("!overflow-hidden")
        document.addEventListener("keydown", this.onKeydown)
        if (window.lenis) window.lenis.stop()

        if (!this.embla) this.initCarousel()
        this.embla?.scrollTo(index, true)

        this.querySelector("[data-zoom-close]")?.focus({ preventScroll: true })
    }

    close() {
        if (!this.isOpen()) return

        // Closing mid-gesture would otherwise leave stale entries that make the
        // next open think fingers are already down.
        this.pointers.clear()
        this.pinch = null
        this.resetZoom()
        this.classList.remove("open")
        this.setAttribute("aria-hidden", "true")
        document.body.classList.remove("!overflow-hidden")
        document.removeEventListener("keydown", this.onKeydown)
        if (window.lenis) window.lenis.start()
    }

    onKeydown(event) {
        if (event.key === "Escape") {
            this.close()
            return
        }
        if (this.zoomed) return
        if (event.key === "ArrowLeft") this.embla?.scrollPrev()
        if (event.key === "ArrowRight") this.embla?.scrollNext()
    }

    // ---------- carousel ----------
    initCarousel() {
        if (typeof EmblaCarousel !== "function") {
            console.warn(
                "product-gallery-zoom: EmblaCarousel unavailable, navigation inert"
            )
            return
        }

        this.embla = EmblaCarousel(this.viewport, {
            axis: "x",
            container: "[data-zoom-track]",
            slides: "[data-zoom-slide]",
            loop: false,
            align: "center",
        })

        // Only drop the zoom when the slide genuinely changed. reInit (used to
        // suspend drag during a pinch) can re-emit select on the same index,
        // which would otherwise cancel the pinch the instant it started.
        this.embla.on("select", () => {
            const index = this.embla.selectedScrollSnap()
            if (index !== this.lastIndex) {
                this.lastIndex = index
                this.resetZoom()
            }
            this.syncControls()
        })

        this.setupDots()
        this.setupArrows()
        this.syncControls()
    }

    setupArrows() {
        this.prevButton?.addEventListener("click", () =>
            this.embla?.scrollPrev()
        )
        this.nextButton?.addEventListener("click", () =>
            this.embla?.scrollNext()
        )
    }

    setupDots() {
        if (!this.dotsContainer) return

        this.dots = this.embla.scrollSnapList().map((_, index) => {
            const dot = document.createElement("button")
            dot.type = "button"
            dot.className = "dot"
            dot.setAttribute("aria-label", `Go to image ${index + 1}`)
            dot.addEventListener("click", () => this.embla.scrollTo(index))
            this.dotsContainer.appendChild(dot)
            return dot
        })
    }

    syncControls() {
        if (!this.embla) return

        const selected = this.embla.selectedScrollSnap()
        this.dots?.forEach((dot, index) =>
            dot.classList.toggle("active", index === selected)
        )

        if (this.prevButton) this.prevButton.disabled = !this.embla.canScrollPrev()
        if (this.nextButton) this.nextButton.disabled = !this.embla.canScrollNext()
    }

    // ---------- zoom ----------
    currentImage() {
        const index = this.embla?.selectedScrollSnap() ?? 0
        return this.slides[index]?.querySelector("img") || null
    }

    contentBox(image) {
        const boxWidth = image.offsetWidth
        const boxHeight = image.offsetHeight
        const naturalWidth = image.naturalWidth || boxWidth
        const naturalHeight = image.naturalHeight || boxHeight
        if (!boxWidth || !boxHeight) return null

        const fit = Math.min(boxWidth / naturalWidth, boxHeight / naturalHeight)
        return {
            width: naturalWidth * fit,
            height: naturalHeight * fit,
            boxWidth,
            boxHeight,
        }
    }

    zoomToPoint(clientX, clientY, scale = this.zoomScale) {
        const image = this.currentImage()
        if (!image) return

        const rect = image.getBoundingClientRect() // untransformed: not zoomed yet
        const content = this.contentBox(image)
        if (!content) return

        const contentLeft = rect.left + (rect.width - content.width) / 2
        const contentTop = rect.top + (rect.height - content.height) / 2
        const clamp01 = (n) => Math.min(1, Math.max(0, n))
        const u = clamp01((clientX - contentLeft) / content.width)
        const v = clamp01((clientY - contentTop) / content.height)

        this.scale = scale
        this.transform = {
            x: (0.5 - u) * content.width * scale,
            y: (0.5 - v) * content.height * scale,
        }

        this.slides.forEach((slide) => slide.classList.remove("is-zoomed"))
        this.currentSlide()?.classList.add("is-zoomed")
        this.syncCursorIcon()
        this.applyTransform()
    }

    currentSlide() {
        const index = this.embla?.selectedScrollSnap() ?? 0
        return this.slides[index] || null
    }


    resetZoom() {
        this.scale = 1
        this.pan = null
        this.pinch = null
        this.transform = { x: 0, y: 0 }
        this.setCarouselDrag(true)
        this.syncCursorIcon()

        this.slides.forEach((slide) => {
            slide.classList.remove("is-zoomed", "is-panning")
            const image = slide.querySelector("img")
            if (image) image.style.transform = ""
        })
    }

    // Keeps the magnified image covering the frame — pan stops at the point
    // where an edge of the painted image would come inside it.
    clampTransform() {
        const image = this.currentImage()
        const slide = this.currentSlide()
        if (!image || !slide) return

        const content = this.contentBox(image)
        if (!content) return

        const frame = slide.getBoundingClientRect()
        const maxX = Math.max(0, (content.width * this.scale - frame.width) / 2)
        const maxY = Math.max(
            0,
            (content.height * this.scale - frame.height) / 2
        )

        this.transform.x = Math.min(maxX, Math.max(-maxX, this.transform.x))
        this.transform.y = Math.min(maxY, Math.max(-maxY, this.transform.y))
    }

    applyTransform() {
        const image = this.currentImage()
        if (!image) return

        this.clampTransform()
        image.style.transform = `translate3d(${this.transform.x.toFixed(1)}px, ${this.transform.y.toFixed(1)}px, 0) scale(${this.scale.toFixed(3)})`
    }

    // ---------- carousel drag suspension ----------
    setCarouselDrag(enabled) {
        if (!this.embla) return
        if (this.carouselDragEnabled === enabled) return
        
        if (this.reinitialising) return

        this.carouselDragEnabled = enabled
        this.reinitialising = true
        try {
            this.embla.reInit({
                watchDrag: enabled,
                startIndex: this.embla.selectedScrollSnap(),
            })
        } finally {
            this.reinitialising = false
        }
    }

    // ---------- pointer: pinch, pan, tap ----------
    pointerList() {
        return Array.from(this.pointers.values())
    }

    pinchMetrics() {
        const [a, b] = this.pointerList()
        return {
            distance: Math.hypot(b.x - a.x, b.y - a.y),
            midX: (a.x + b.x) / 2,
            midY: (a.y + b.y) / 2,
        }
    }

    frameCentre() {
        const slide = this.currentSlide()
        if (!slide) return null
        const rect = slide.getBoundingClientRect()
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
    }

    startPinch() {
        const centre = this.frameCentre()
        if (!centre) return

        const { distance, midX, midY } = this.pinchMetrics()
        if (!distance) return

        this.pan = null // the pinch takes the gesture over from any pan
        this.currentSlide()?.classList.add("is-panning") // kills the transition
        this.setCarouselDrag(false)

        this.pinch = {
            startDistance: distance,
            startScale: this.scale,
            
            anchorX: (midX - centre.x - this.transform.x) / this.scale,
            anchorY: (midY - centre.y - this.transform.y) / this.scale,
        }
    }

    updatePinch(event) {
        const centre = this.frameCentre()
        if (!centre) return

        const { distance, midX, midY } = this.pinchMetrics()
        const ratio = distance / this.pinch.startDistance

        this.scale = Math.min(
            this.maxScale,
            Math.max(1, this.pinch.startScale * ratio)
        )
        this.transform = {
            x: midX - centre.x - this.pinch.anchorX * this.scale,
            y: midY - centre.y - this.pinch.anchorY * this.scale,
        }

        this.slides.forEach((slide) => slide.classList.remove("is-zoomed"))
        if (this.zoomed) this.currentSlide()?.classList.add("is-zoomed")
        this.syncCursorIcon()

        event.preventDefault()
        this.applyTransform()
    }

    endPinch() {
        this.pinch = null
        this.currentSlide()?.classList.remove("is-panning")

        // Pinched back to (or below) fit: drop out of zoom entirely so swiping
        // between images works again.
        if (this.scale <= 1) this.resetZoom()
        else this.setCarouselDrag(false)
    }

    onPointerDown(event) {
        if (event.pointerType === "mouse" && event.button !== 0) return
        if (event.target.closest("button")) return

        this.pointers.set(event.pointerId, {
            x: event.clientX,
            y: event.clientY,
        })

        if (this.zoomed) event.stopImmediatePropagation()

        if (this.pointers.size === 2) {
            this.startPinch()
            return
        }

        if (this.pointers.size > 2) return

        this.pan = {
            startX: event.clientX,
            startY: event.clientY,
            lastX: event.clientX,
            lastY: event.clientY,
            distance: 0,
        }
    }

    onPointerMove(event) {
        if (!this.pointers.has(event.pointerId)) return

        this.pointers.set(event.pointerId, {
            x: event.clientX,
            y: event.clientY,
        })

        if (this.pinch && this.pointers.size >= 2) {
            this.updatePinch(event)
            return
        }

        if (!this.pan) return

        const dx = event.clientX - this.pan.lastX
        const dy = event.clientY - this.pan.lastY
        this.pan.lastX = event.clientX
        this.pan.lastY = event.clientY
        this.pan.distance += Math.abs(dx) + Math.abs(dy)

        if (!this.zoomed) return // let Embla have the gesture

        if (this.pan.distance > this.dragThreshold) {
            this.currentSlide()?.classList.add("is-panning")
            event.preventDefault()
        }

        this.transform.x += dx
        this.transform.y += dy
        this.applyTransform()
    }

    onPointerUp(event) {
        const tracked = this.pointers.delete(event.pointerId)
        if (!tracked) return

        if (this.pinch) {
            if (this.pointers.size < 2) this.endPinch()
            // Lifting out of a pinch must never register as a tap.
            this.pan = null
            return
        }

        if (!this.pan) return

        const wasTap = this.pan.distance <= this.dragThreshold
        this.pan = null
        this.currentSlide()?.classList.remove("is-panning")

        if (!wasTap) return

        if (event.pointerType === "mouse") {
            this.toggleZoomAt(event.clientX, event.clientY, this.zoomScale)
            return
        }

        const now = performance.now()
        const previous = this.lastTap
        const isDouble =
            previous &&
            now - previous.time < this.doubleTapMs &&
            Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <
                this.doubleTapDistance

        if (isDouble) {
            this.lastTap = null
            this.toggleZoomAt(
                event.clientX,
                event.clientY,
                this.mobileZoomScale
            )
            return
        }

        this.lastTap = { time: now, x: event.clientX, y: event.clientY }
    }

    toggleZoomAt(clientX, clientY, scale) {
        if (this.zoomed) {
            this.resetZoom()
            return
        }
        this.zoomToPoint(clientX, clientY, scale)
        this.setCarouselDrag(false)
    }
}

if (!customElements.get("product-gallery-zoom")) {
    customElements.define("product-gallery-zoom", ProductGalleryZoom)
}
